import { useEffect, useState } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { getPlans, signup } from '../../api/auth'
import PasswordField from '../../components/PasswordField'
import TimeZoneSelect from '../../components/TimeZoneSelect'
import AuthPage, { AuthField } from '../../components/PublicLayout'
import { rememberCompanyId } from '../../utils/companyId'
import { deviceZone } from '../../utils/timeZones'
import { LINE_COPY, LineSwitch } from '../../components/PlanCards'

/** A whole number from 0 to 100 with − and + buttons. */
function Stepper({ id, label, hint, value, onChange }) {
  const set = (n) => onChange(Math.max(0, Math.min(100, Number.isFinite(n) ? n : 0)))
  return (
    <div className="pub-stepper">
      <label htmlFor={id}>{label}<span className="pub-field-hint">{hint}</span></label>
      <div className="pub-stepper-box">
        <button type="button" onClick={() => set(value - 1)} disabled={value <= 0} aria-label={`Fewer ${label.toLowerCase()}`}>−</button>
        <input id={id} inputMode="numeric" value={value} onChange={(e) => set(parseInt(e.target.value.replace(/\D/g, '') || '0', 10))} />
        <button type="button" onClick={() => set(value + 1)} aria-label={`More ${label.toLowerCase()}`}>+</button>
      </div>
    </div>
  )
}

export default function Signup({ onSignedIn }) {
  const nav = useNavigate()
  const [companyName, setCompanyName] = useState('')
  const [businessType, setBusinessType] = useState('')
  // The company's clock: this device's to start with, as it's usually where the business is.
  const [timeZone, setTimeZone] = useState(() => deviceZone())
  // The plan chosen on the pricing page (?plan=), and its add-ons.
  const [params] = useSearchParams()
  const [catalogue, setCatalogue] = useState(null)
  const [plan, setPlan] = useState(params.get('plan') || 'invoice_basic')
  // The line of business shown, from the plan in the link (store_plus -> store).
  const [line, setLine] = useState((params.get('plan') || 'invoice_basic').split('_')[0])
  const [addons, setAddons] = useState([])
  const [extraUsers, setExtraUsers] = useState(0)
  const [extraLocations, setExtraLocations] = useState(0)
  useEffect(() => { getPlans().then(setCatalogue).catch(() => {}) }, [])
  const chosen = catalogue?.plans?.find((p) => p.id === plan)
  const monthly = chosen ? chosen.price + chosen.addons.filter((a) => addons.includes(a.key)).reduce((t, a) => t + a.price, 0)
    + extraUsers * (catalogue?.extra_user || 0) + extraLocations * (catalogue?.extra_location || 0) : 0
  const pickPlan = (id) => { setPlan(id); setAddons([]) }
  // Switching line keeps the same tier: Store Advanced becomes Invoice Advanced.
  const pickLine = (id) => {
    setLine(id)
    const tier = chosen?.tier || 'basic'
    const same = catalogue?.plans?.find((p) => p.line === id && p.tier === tier)
    if (same) pickPlan(same.id)
  }
  const toggleAddon = (key) => setAddons((list) => (list.includes(key) ? list.filter((k) => k !== key) : [...list, key]))
  const [fullName, setFullName] = useState('')
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [emailErr, setEmailErr] = useState('')
  const looksLikeEmail = (v) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(v || '').trim())

  const validateEmail = (value) => {
    const v = String(value || '').trim()
    if (!v) return 'Enter your email address.'
    if (!looksLikeEmail(v)) return 'That doesn’t look like an email address, e.g. name@example.com.'
    return ''
  }

  const submit = async (e) => {
    e && e.preventDefault()
    if (!companyName || !businessType || !fullName || !username || !email || !password || !confirmPassword) {
      setErr('Fill in every field to create your company.')
      return
    }
    const currentEmailErr = validateEmail(email)
    if (currentEmailErr) {
      setEmailErr(currentEmailErr)
      return
    }
    if (password.length < 8) {
      setErr('Use a password of at least 8 characters.')
      return
    }
    if (password !== confirmPassword) {
      setErr('The two passwords don’t match.')
      return
    }
    setBusy(true); setErr('')
    try {
      const normalizedEmail = String(email || '').trim().toLowerCase()
      const u = await signup({ company_name: companyName, business_type: businessType, time_zone: timeZone, full_name: fullName, username, email: normalizedEmail, password, plan, addons, extra_users: extraUsers, extra_locations: extraLocations })
      rememberCompanyId(u?.tenant_id)
      onSignedIn && onSignedIn(u)
      if (u?.welcome_email_sent === false) {
        const idTxt = u?.tenant_id != null ? ` Your Company ID is ${u.tenant_id}. Note it down: you’ll need it to sign in on another device.` : ''
        const reasonTxt = u?.welcome_email_error ? ` (${u.welcome_email_error})` : ''
        window.alert(`Your company is ready, but the welcome email didn’t send.${idTxt}${reasonTxt}`)
      }
      // Straight on to setting the company up.
      nav('/onboarding')
    } catch (e) {
      setErr(String(e?.response?.data?.message || e?.response?.data?.error || e?.message || e))
    } finally { setBusy(false) }
  }

  return (
    <AuthPage
      title="Create your company"
      intro="You’ll be its administrator, and can add your staff afterwards."
      top={<Link className="pub-btn pub-btn-ghost" to="/login">Sign in</Link>}
      foot={<>Already using ASYS Retail? <Link to="/login">Sign in</Link><br />An accountant or bookkeeper? <Link to="/accountants/signup">Create a free practice</Link></>}
    >
      <form onSubmit={submit} className="pub-form" noValidate>
        <p className="pub-group">Your company</p>
        <AuthField id="signup-company-name" label="Company name">
          <input id="signup-company-name" value={companyName} onChange={(e) => setCompanyName(e.target.value)} autoComplete="organization" autoFocus />
        </AuthField>
        <AuthField id="signup-business-type" label="Who owns it?" hint="Sets up the owner’s accounts in your books: capital and drawings for an owner, share capital and dividends for a company. You can change it until something is posted to them.">
          <select id="signup-business-type" value={businessType} onChange={(e) => setBusinessType(e.target.value)}>
            <option value="">Choose one…</option>
            <option value="sole">A sole owner or partnership</option>
            <option value="company">A company with shares (LLC, Ltd, Inc)</option>
          </select>
        </AuthField>
        <AuthField id="signup-time-zone" label="Time zone" hint="Where your business is. Dates and times on everything you record follow its clock; you can change it later in Settings.">
          <TimeZoneSelect id="signup-time-zone" value={timeZone} onChange={setTimeZone} />
        </AuthField>

        {catalogue?.plans?.length > 0 && (
          <>
            <p className="pub-group">Your plan</p>
            <LineSwitch lines={catalogue.lines} value={line} onChange={pickLine} />
            <p className="pub-field-hint">{LINE_COPY[line]}</p>
            <div className="pub-plan-pick" role="radiogroup" aria-label="Plan">
              {catalogue.plans.filter((p) => p.line === line).map((p) => (
                <label key={p.id}>
                  <input type="radio" name="plan" value={p.id} checked={plan === p.id} onChange={() => pickPlan(p.id)} />
                  <strong>{catalogue.tiers.find((t) => t.id === p.tier)?.label}</strong>
                  <span>${p.price}/month</span>
                </label>
              ))}
            </div>
            {chosen?.addons?.length > 0 && (
              <div className="pub-addon-pick">
                {chosen.addons.map((a) => (
                  <label key={a.key}>
                    <input type="checkbox" checked={addons.includes(a.key)} onChange={() => toggleAddon(a.key)} />
                    {a.label} <span className="pub-field-hint">+${a.price}/month</span>
                  </label>
                ))}
              </div>
            )}
            <div className="pub-extras">
              <Stepper id="signup-extra-users" label="Extra users" hint={`${chosen?.users ?? 2} included; cashiers are free. +$${catalogue.extra_user}/month each.`} value={extraUsers} onChange={setExtraUsers} />
              <Stepper id="signup-extra-locations" label="Extra locations" hint={`${chosen?.locations ?? 1} included. +$${catalogue.extra_location}/month each.`} value={extraLocations} onChange={setExtraLocations} />
            </div>
            <p className="pub-plan-total">{chosen?.label}: free for {catalogue.trial_days} days, then <strong>${monthly}/month</strong>. {(chosen?.users ?? 0) + extraUsers} users (cashiers free) and {(chosen?.locations ?? 1) + extraLocations} location{(chosen?.locations ?? 1) + extraLocations === 1 ? '' : 's'}. <Link to={`/?line=${line}#pricing`}>Compare plans</Link></p>
          </>
        )}

        <p className="pub-group">You</p>
        <AuthField id="signup-full-name" label="Full name">
          <input id="signup-full-name" value={fullName} onChange={(e) => setFullName(e.target.value)} autoComplete="name" />
        </AuthField>
        <AuthField id="signup-email" label="Email" hint="We’ll send your welcome email here, with the number you sign in with." error={emailErr}>
          <input
            id="signup-email"
            type="email"
            value={email}
            onChange={(e) => {
              const next = e.target.value
              setEmail(next)
              if (emailErr) setEmailErr(validateEmail(next))
            }}
            onBlur={() => setEmailErr(email ? validateEmail(email) : '')}
            autoComplete="email"
          />
        </AuthField>
        <AuthField id="signup-username" label="Username" hint="What you’ll sign in with.">
          <input id="signup-username" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" />
        </AuthField>
        <div className="pub-row">
          <AuthField id="signup-password" label="Password" hint="At least 8 characters.">
            <PasswordField id="signup-password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
          </AuthField>
          <AuthField id="signup-confirm-password" label="Password again">
            <PasswordField id="signup-confirm-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" />
          </AuthField>
        </div>

        {err && <p className="pub-error" role="alert">{err}</p>}
        <button type="submit" className="pub-btn pub-btn-primary pub-btn-block" disabled={busy}>{busy ? 'Creating your company…' : 'Create your company'}</button>
      </form>
    </AuthPage>
  )
}
